import {
  useCallback,
  useEffect,
  useMemo,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type {
  DraftPair,
  DraftPlayer,
  PairMode,
} from "../../challenges/types/challengeDraft";
import {
  normalizeChallengeRegistrationCategoriesForEdit,
  normalizeTournamentRegistrationCategoriesForEdit,
} from "../utils/tournamentCategories";

type UseAdminChallengeTournamentCategorySelectionParams = {
  activeTournamentCategoryKey: string;
  challengeDetail: ChallengeDetail | null;
  isPoco: boolean;
  isSuper8: boolean;
  pairMode: PairMode;
  setActiveTournamentCategoryKey: Dispatch<SetStateAction<string>>;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setDraftPairs: Dispatch<SetStateAction<DraftPair[]>>;
  setDraftPlayers: Dispatch<SetStateAction<DraftPlayer[]>>;
  setDrawCpf: Dispatch<SetStateAction<string>>;
  setPairCpf1: Dispatch<SetStateAction<string>>;
  setPairCpf2: Dispatch<SetStateAction<string>>;
  tournamentDraftCategoryKeyRef: MutableRefObject<string>;
};

export function useAdminChallengeTournamentCategorySelection({
  activeTournamentCategoryKey,
  challengeDetail,
  isPoco,
  isSuper8,
  pairMode,
  setActiveTournamentCategoryKey,
  setChallengeDetail,
  setDraftPairs,
  setDraftPlayers,
  setDrawCpf,
  setPairCpf1,
  setPairCpf2,
  tournamentDraftCategoryKeyRef,
}: UseAdminChallengeTournamentCategorySelectionParams) {
  const isTournamentChallenge =
    String(challengeDetail?.challengeType || "")
      .trim()
      .toLowerCase() === "tournament";
  const isPocoChallenge =
    String(challengeDetail?.challengeType || "")
      .trim()
      .toLowerCase() === "poco";
  const hasRegistrationCategories =
    isTournamentChallenge ||
    isPocoChallenge ||
    (String(challengeDetail?.challengeType || "").trim().toLowerCase() ===
      "challenge" &&
      (challengeDetail?.registrationCategories?.length ?? 0) > 0);
  const challengeTournamentCategories = useMemo(
    () => {
      if (!hasRegistrationCategories || !challengeDetail) return [];
      return isPocoChallenge
        ? normalizeChallengeRegistrationCategoriesForEdit(challengeDetail)
        : normalizeTournamentRegistrationCategoriesForEdit(
            challengeDetail.registrationCategories,
          );
    },
    [challengeDetail, hasRegistrationCategories, isPocoChallenge],
  );
  const activeTournamentCategory = useMemo(() => {
    if (!hasRegistrationCategories) return null;
    const safeKey = String(activeTournamentCategoryKey || "").trim();
    if (safeKey) {
      const matched = challengeTournamentCategories.find(
        (item) => item.categoryKey === safeKey,
      );
      if (matched) return matched;
    }
    return challengeTournamentCategories[0] || null;
  }, [
    activeTournamentCategoryKey,
    challengeTournamentCategories,
    hasRegistrationCategories,
  ]);
  const activeRegistrationCategoryKeyForModal = hasRegistrationCategories
    ? String(activeTournamentCategory?.categoryKey || "").trim()
    : "";

  const resetTournamentSelectionDrafts = useCallback(() => {
    setPairCpf1("");
    setPairCpf2("");
    setDrawCpf("");
    setDraftPairs([]);
    setDraftPlayers([]);
    setChallengeDetail((prev) =>
      prev ? { ...prev, participantCpfs: [] } : prev,
    );
  }, [
    setChallengeDetail,
    setDraftPairs,
    setDraftPlayers,
    setDrawCpf,
    setPairCpf1,
    setPairCpf2,
  ]);

  const selectTournamentCategoryForRegistrations = useCallback(
    (categoryKey: string) => {
      const nextKey = String(categoryKey || "").trim();
      const currentKey = String(
        activeRegistrationCategoryKeyForModal || "",
      ).trim();
      if (nextKey === currentKey) return;
      tournamentDraftCategoryKeyRef.current = nextKey;
      resetTournamentSelectionDrafts();
      setActiveTournamentCategoryKey(nextKey);
    },
    [
      activeRegistrationCategoryKeyForModal,
      resetTournamentSelectionDrafts,
      setActiveTournamentCategoryKey,
      tournamentDraftCategoryKeyRef,
    ],
  );

  useEffect(() => {
    if (!hasRegistrationCategories) {
      tournamentDraftCategoryKeyRef.current = "";
      return;
    }
    if (
      activeRegistrationCategoryKeyForModal &&
      !tournamentDraftCategoryKeyRef.current
    ) {
      tournamentDraftCategoryKeyRef.current =
        activeRegistrationCategoryKeyForModal;
    }
  }, [
    activeRegistrationCategoryKeyForModal,
    hasRegistrationCategories,
    tournamentDraftCategoryKeyRef,
  ]);

  useEffect(() => {
    if (!hasRegistrationCategories) {
      setActiveTournamentCategoryKey("");
      return;
    }
    if (challengeTournamentCategories.length === 0) {
      setActiveTournamentCategoryKey("");
      return;
    }
    const activeKey = String(activeTournamentCategoryKey || "").trim();
    if (
      activeKey &&
      challengeTournamentCategories.some(
        (item) => item.categoryKey === activeKey,
      )
    ) {
      return;
    }
    setActiveTournamentCategoryKey(
      String(challengeTournamentCategories[0]?.categoryKey || ""),
    );
  }, [
    activeTournamentCategoryKey,
    challengeTournamentCategories,
    hasRegistrationCategories,
    setActiveTournamentCategoryKey,
  ]);

  const currentPairMode: PairMode = isSuper8
    ? "draw"
    : isPoco
      ? "fixed"
      : hasRegistrationCategories
        ? activeTournamentCategory?.registrationType === "fixed_pair"
          ? "fixed"
          : "draw"
        : pairMode;

  return {
    activeRegistrationCategoryKeyForModal,
    activeTournamentCategory,
    challengeTournamentCategories,
    currentPairMode,
    hasRegistrationCategories,
    isTournamentChallenge,
    selectTournamentCategoryForRegistrations,
  };
}
